import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Close';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import PrivacyIcon from '@mui/icons-material/ShieldOutlined';

import { customerService } from '../../../services/api';
import { parseApiFormErrors } from '../../../services/apiErrors';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import usePermissionsCatalog from './usePermissionsCatalog';
import { grantedCount, toggleCodes } from './permissionGroups';

export default function PermissionsPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const roleId = searchParams.get('role');
  const groupKey = searchParams.get('group_key') ?? '';

  const catalog = usePermissionsCatalog();

  const rolesQuery = useQuery({
    queryKey: ['roles'],
    queryFn: () => customerService.getRoles().then((res) => res.data),
  });
  const roles = (rolesQuery.data ?? []).filter((role) => !role.is_deleted);

  const roleQuery = useQuery({
    queryKey: ['role', roleId],
    queryFn: () => customerService.getRole(roleId).then((res) => res.data),
    enabled: Boolean(roleId),
  });
  const role = roleQuery.data;
  const codes = role?.permissions ?? [];

  const [saveError, setSaveError] = useState('');

  // Keeps the other param when one changes (role and group_key are independent).
  // Other pages link here with both: utils/permissionsUrl.
  const setParam = (key, value, options) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      options,
    );
  };

  // No role in the URL: open the first one, so the page never starts empty.
  useEffect(() => {
    if (!roleId && roles.length > 0) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('role', roles[0].uuid);
          return next;
        },
        { replace: true },
      );
    }
  }, [roleId, roles, setSearchParams]);

  // The API says what the caller may change (core/policies/role.py): never
  // the owner role or a role they hold, and core.role.* codes only if they
  // hold them.
  const authorization = role?.authorization;
  const readOnly = !authorization?.capabilities?.can_edit_permissions;
  const lockedCodes = new Set(authorization?.locked_permissions ?? []);

  // Every change is saved on its own. Changes to the same role run one after
  // the other (scope), each sending the whole list as it stood after the
  // click, so the last one always leaves the role as the page shows it.
  const mutationKey = ['rolePermissions', roleId];
  const savingCount = useIsMutating({ mutationKey });
  const mutation = useMutation({
    mutationKey,
    scope: { id: `rolePermissions:${roleId}` },
    mutationFn: ({ uuid, permissions }) => customerService.patchRole(uuid, { permissions }),
    onError: (error) => {
      const { fieldErrors, formError } = parseApiFormErrors(error, ['permissions']);
      setSaveError(fieldErrors.permissions || formError || t('permissions.saveError'));
    },
    onSettled: (_data, _error, { uuid }) => {
      // Only the last pending change reloads the role, so an earlier answer
      // never undoes a later click on screen.
      if (queryClient.isMutating({ mutationKey: ['rolePermissions', uuid] }) === 1) {
        queryClient.invalidateQueries({ queryKey: ['role', uuid] });
        queryClient.invalidateQueries({ queryKey: ['roles'] });
      }
    },
  });

  const setCodes = (toggled, checked) => {
    // Read from the cache, not the render: clicks faster than a re-render
    // must still build on each other.
    const current = queryClient.getQueryData(['role', role.uuid])?.permissions ?? [];
    const permissions = toggleCodes(current, toggled, checked);
    setSaveError('');
    queryClient.cancelQueries({ queryKey: ['role', role.uuid] });
    queryClient.setQueryData(['role', role.uuid], (prev) => ({ ...prev, permissions }));
    mutation.mutate({ uuid: role.uuid, permissions });
  };

  const handleRoleChange = (uuid) => {
    setSaveError('');
    setParam('role', uuid);
  };

  // An unknown group_key (e.g. an old link) shows them all.
  const sortedGroups = [...catalog.groups].sort((a, b) => a.group.localeCompare(b.group, i18n.language));
  const selectedGroup = sortedGroups.find((group) => group.key === groupKey);
  const visibleGroups = selectedGroup ? [selectedGroup] : sortedGroups;

  const header = (
    <PageHeader
      icon={<KeyOutlinedIcon />}
      title={t('permissions.title')}
      breadcrumbs={[
        { label: t('roles.breadcrumb.dashboard') },
        { label: t('nav.groups.team') },
        { label: t('permissions.title') },
      ]}
      actions={
        role &&
        !readOnly && (
          <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
            {savingCount > 0 ? <CircularProgress size={16} color="inherit" /> : <CheckIcon fontSize="small" />}
            <Typography variant="body2">
              {savingCount > 0 ? t('permissions.saving') : t('permissions.autosaveHint')}
            </Typography>
          </Stack>
        )
      }
    />
  );

  const renderGroups = () => {
    if (roleQuery.isLoading || catalog.isLoading) {
      return <LoadingState message={t('permissions.states.loading')} />;
    }
    if (roleQuery.isError) {
      const status = roleQuery.error?.response?.status;
      if (status === 403) {
        return (
          <PermissionDenied
            title={t('permissions.states.permissionDeniedTitle')}
            message={t('permissions.states.permissionDeniedMessage')}
          />
        );
      }
      return (
        <ErrorState
          title={t('permissions.states.errorTitle')}
          message={status === 404 ? t('permissions.states.roleNotFound') : t('permissions.states.errorMessage')}
          actionLabel={t('permissions.states.retry')}
          onRetry={roleQuery.refetch}
        />
      );
    }
    if (catalog.isError) {
      return (
        <ErrorState
          title={t('permissions.states.errorTitle')}
          message={t('permissions.states.catalogError')}
          actionLabel={t('permissions.states.retry')}
          onRetry={catalog.refetch}
        />
      );
    }
    if (!role) return null;

    return visibleGroups.map((group) => {
      const granted = grantedCount(group.permissions, codes);
      // "Mark all"/"Clear" leaves the locked codes as they are.
      const editableCodes = group.permissions
        .map((permission) => permission.code)
        .filter((code) => !lockedCodes.has(code));
      const allGranted = editableCodes.every((code) => codes.includes(code));
      return (
        <Card key={group.key}>
          <CardHeader
            title={group.group}
            subheader={t('permissions.groupCount', { granted, total: group.permissions.length })}
            titleTypographyProps={{ variant: 'h6' }}
            action={
              !readOnly &&
              editableCodes.length > 0 && (
                <Button size="small" onClick={() => setCodes(editableCodes, !allGranted)} sx={{ mt: 1 }}>
                  {allGranted ? t('permissions.actions.clearAll') : t('permissions.actions.markAll')}
                </Button>
              )
            }
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              {group.permissions.map((permission, index) => (
                <ListItem
                  key={permission.code}
                  disableGutters
                  divider={index < group.permissions.length - 1}
                  secondaryAction={
                    <Switch
                      edge="end"
                      checked={codes.includes(permission.code)}
                      onChange={(event) => setCodes([permission.code], event.target.checked)}
                      disabled={readOnly || lockedCodes.has(permission.code)}
                      slotProps={{ input: { role: 'switch', 'aria-label': permission.name } }}
                    />
                  }
                >
                  <ListItemText
                    primary={
                      permission.privacy ? (
                        <Stack component="span" direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                          <span>{permission.name}</span>
                          <Tooltip title={t('permissions.privacy.tooltip')}>
                            <Chip
                              size="small"
                              variant="outlined"
                              color="warning"
                              icon={<PrivacyIcon />}
                              label={t('permissions.privacy.label')}
                              tabIndex={0}
                            />
                          </Tooltip>
                        </Stack>
                      ) : (
                        permission.name
                      )
                    }
                    secondary={
                      <>
                        {permission.description && (
                          <Typography variant="body2" color="text.secondary" component="span" display="block">
                            {permission.description}
                          </Typography>
                        )}
                        {!readOnly && lockedCodes.has(permission.code) && (
                          <Typography variant="body2" color="warning.main" component="span" display="block">
                            {t('permissions.lockedHint')}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.disabled" component="span" fontFamily="monospace">
                          {permission.code}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      );
    });
  };

  const renderBody = () => {
    if (rolesQuery.isLoading) {
      return <LoadingState message={t('permissions.states.loading')} />;
    }
    if (rolesQuery.isError) {
      return (
        <ErrorState
          title={t('permissions.states.errorTitle')}
          message={t('permissions.states.errorMessage')}
          actionLabel={t('permissions.states.retry')}
          onRetry={rolesQuery.refetch}
        />
      );
    }

    const total = catalog.codes.length;
    const granted = grantedCount(catalog.groups.flatMap((group) => group.permissions), codes);

    return (
      <Stack spacing={3} sx={{ width: '100%', pb: 3 }}>
        <Card>
          <CardHeader
            title={t('permissions.sections.role')}
            subheader={t('permissions.sections.roleHint')}
            titleTypographyProps={{ variant: 'h6' }}
            action={
              role &&
              total > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  label={t('permissions.totalCount', { granted, total })}
                  sx={{ mt: 1 }}
                />
              )
            }
          />
          <Divider />
          <CardContent>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  label={t('permissions.fields.role')}
                  helperText={t('permissions.fields.roleHelper')}
                  value={roles.some((option) => option.uuid === roleId) ? roleId : ''}
                  onChange={(event) => handleRoleChange(event.target.value)}
                  fullWidth
                >
                  {roles.map((option) => (
                    <MenuItem key={option.uuid} value={option.uuid}>
                      {option.is_active ? option.name : `${option.name} (${t('roles.statuses.inactive')})`}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label={t('permissions.fields.section')}
                  helperText={t('permissions.fields.sectionHelper')}
                  value={selectedGroup ? groupKey : ''}
                  onChange={(event) => setParam('group_key', event.target.value)}
                  fullWidth
                  slotProps={{
                    input: {
                      endAdornment: selectedGroup && (
                        // mr: abre espaço para a seta do select, que fica por cima à direita.
                        <InputAdornment position="end" sx={{ mr: 2.5 }}>
                          <IconButton
                            size="small"
                            edge="end"
                            aria-label={t('filterBar.clearField', { field: t('permissions.fields.section') })}
                            onClick={() => setParam('group_key', '')}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                >
                  {sortedGroups.map((group) => (
                    <MenuItem key={group.key} value={group.key}>
                      {group.group}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              {role && readOnly && (
                <Alert severity="info">{t(`permissions.lockReasons.${authorization?.permissions_lock_reason ?? 'no_permission'}`)}</Alert>
              )}
              {catalog.failedServices.length > 0 && !catalog.isError && (
                <Alert severity="warning">{t('permissions.partialCatalog')}</Alert>
              )}
              {saveError && <Alert severity="error">{saveError}</Alert>}
            </Stack>
          </CardContent>
        </Card>

        {renderGroups()}
      </Stack>
    );
  };

  return (
    <BoundedPageLayout header={header} maxWidth="md">
      {renderBody()}

    </BoundedPageLayout>
  );
}
